import axios, { type AxiosError, type AxiosInstance } from 'axios';
import { ApiError, type RetryableRequestConfig } from '../../api.types';
import { shouldRetryRequest } from './shouldRetryRequest';
import { retryRequest } from './retryRequest';
import { logFailedRequest } from './logFailedRequest';
import { authService } from '@/features/auth/services/AuthService';
import { logger } from '@/infra/logging';
import { dispatchSessionExpired } from '@/infra/auth/auth.events';
import { dispatchApiError, categorizeApiError } from '../../api.events';

const HTTP_STATUS_UNAUTHORIZED = 401;

let logoutPromise: Promise<void> | null = null;

export async function handleResponseError(
  error: AxiosError<{ message?: string; error?: string }>,
  axiosInstance: AxiosInstance
): Promise<never> {
  if (axios.isCancel(error)) {
    return Promise.reject(error);
  }

  const originalRequest = error.config as RetryableRequestConfig;
  const status = error.response?.status;
  // Most controllers in this API return errors as { error: "..." }, not { message: "..." } --
  // without this fallback, ApiError.message silently drops to axios's generic
  // "Request failed with status code NNN" for every one of them.
  const message =
    error.response?.data?.message ?? error.response?.data?.error ?? error.message ?? 'An unexpected error occurred';

  // Handle 401 Unauthorized - session expired
  if (status === HTTP_STATUS_UNAUTHORIZED && !originalRequest._tokenRetry) {
    const url = originalRequest.url ?? '';
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/logout') || url.includes('/auth/me');

    if (!isAuthEndpoint && !logoutPromise) {
      logger.warn('Session expired (401), logging out user', { component: 'handleResponseError' });

      logoutPromise = (async () => {
        try {
          await authService.logout();
        } catch (logoutError) {
          logger.error('Failed to logout on 401', logoutError as Error, { component: 'handleResponseError' });
        } finally {
          const currentPath = window.location.pathname;
          if (!currentPath.startsWith('/auth/login') && !currentPath.startsWith('/auth/logout')) {
            dispatchSessionExpired(currentPath);
          }

          setTimeout(() => {
            logoutPromise = null;
          }, 1000);
        }
      })();
    }

    return Promise.reject(new ApiError(message, status, error.code, error.response?.data));
  }

  // Retry logic for network errors and 5xx server errors
  if (shouldRetryRequest(originalRequest, status, error.code)) {
    return retryRequest(originalRequest, axiosInstance);
  }

  logFailedRequest(error, originalRequest, status);

  // Dispatch API error event for toast notifications
  const errorType = categorizeApiError(status, error.code);
  if (errorType !== 'unknown') {
    dispatchApiError({
      type: errorType,
      message,
      ...(status !== undefined && { status }),
      ...(error.code !== undefined && { code: error.code }),
    });
  }

  return Promise.reject(new ApiError(message, status, error.code, error.response?.data));
}
